import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { submitDemoEnquiry } from "../../Moduels/demo/bookDemoApi";
import DemoChrome from "./DemoChrome";
import "./auth.css";
import "./bookDemo.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^(\+91[\-\s]?)?[6-9]\d{9}$/;

export default function BookADemoPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    fullName: "",
    workEmail: "",
    organizationName: "",
    phoneNumber: "",
  });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [booking, setBooking] = useState(false);

  const field = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
    setFormError("");
  };

  const validate = () => {
    const next = {};
    const email = form.workEmail.trim();
    if (!email) next.workEmail = "Work email is required.";
    else if (!EMAIL_RE.test(email)) next.workEmail = "Enter a valid work email.";

    const phone = form.phoneNumber.trim();
    if (phone && !PHONE_RE.test(phone.replace(/\s/g, ""))) {
      next.phoneNumber = "Enter a valid 10-digit mobile number.";
    }
    if (form.fullName.trim().length > 200) next.fullName = "Please use 200 characters or fewer.";
    if (form.organizationName.trim().length > 200) {
      next.organizationName = "Please use 200 characters or fewer.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleBookDemo = async (e) => {
    e.preventDefault();
    if (booking) return;
    if (!validate()) return;

    setBooking(true);
    setFormError("");
    try {
      const result = await submitDemoEnquiry({
        work_email: form.workEmail.trim(),
        full_name: form.fullName.trim() || null,
        organization_name: form.organizationName.trim() || null,
        phone_number: form.phoneNumber.trim() || null,
      });
      const token = result?.access_token;
      if (!token) {
        setFormError("We could not save your request. Please try again.");
        return;
      }
      navigate(`/book-demo/success?token=${encodeURIComponent(token)}`, { replace: true });
    } catch (err) {
      setFormError(err?.message || "We could not save your request. Please try again.");
    } finally {
      setBooking(false);
    }
  };

  return (
    <DemoChrome backLabel="Back to Home">
      <div className="bd-hero">
        <span className="bd-eyebrow">Schedule a walkthrough</span>
        <h1 className="bd-title">
          Book a <span>Demo</span>
        </h1>
        <p className="bd-subtitle">
          Tell us how to reach you. Scheduling a time is optional — you can choose a slot after we receive your request.
        </p>
      </div>

      <div className="bd-card bd-card--form">
        <form onSubmit={handleBookDemo} noValidate>
          <div className="bd-form-grid">
            <div className="bd-field bd-field--full">
              <label className="bd-label" htmlFor="bd-email">
                Work Email <span className="bd-star">*</span>
              </label>
              <div className="field-wrapper">
                <input
                  id="bd-email"
                  type="email"
                  autoComplete="email"
                  className={`field-input ${errors.workEmail ? "input-error" : ""}`}
                  placeholder="you@company.com"
                  value={form.workEmail}
                  onChange={field("workEmail")}
                  disabled={booking}
                />
              </div>
              {errors.workEmail && <p className="reg-err">{errors.workEmail}</p>}
            </div>

            <div className="bd-field">
              <label className="bd-label" htmlFor="bd-name">
                Full Name
              </label>
              <div className="field-wrapper">
                <input
                  id="bd-name"
                  type="text"
                  autoComplete="name"
                  maxLength={200}
                  className={`field-input ${errors.fullName ? "input-error" : ""}`}
                  placeholder="Your name"
                  value={form.fullName}
                  onChange={field("fullName")}
                  disabled={booking}
                />
              </div>
              {errors.fullName && <p className="reg-err">{errors.fullName}</p>}
            </div>

            <div className="bd-field">
              <label className="bd-label" htmlFor="bd-org">
                Company Name
              </label>
              <div className="field-wrapper">
                <input
                  id="bd-org"
                  type="text"
                  autoComplete="organization"
                  maxLength={200}
                  className={`field-input ${errors.organizationName ? "input-error" : ""}`}
                  placeholder="Company name"
                  value={form.organizationName}
                  onChange={field("organizationName")}
                  disabled={booking}
                />
              </div>
              {errors.organizationName && <p className="reg-err">{errors.organizationName}</p>}
            </div>

            <div className="bd-field bd-field--full">
              <label className="bd-label" htmlFor="bd-phone">
                Phone Number
              </label>
              <div className="field-wrapper">
                <input
                  id="bd-phone"
                  type="tel"
                  autoComplete="tel"
                  className={`field-input ${errors.phoneNumber ? "input-error" : ""}`}
                  placeholder="9876543210"
                  value={form.phoneNumber}
                  onChange={field("phoneNumber")}
                  disabled={booking}
                />
              </div>
              {errors.phoneNumber && <p className="reg-err">{errors.phoneNumber}</p>}
            </div>
          </div>

          {formError && (
            <p className="bd-form-error" role="alert">{formError}</p>
          )}

          <div className="bd-actions">
            <button type="submit" className={`bd-submit ${booking ? "loading" : ""}`} disabled={booking}>
              {booking ? (
                <><span className="spinner" /> Booking…</>
              ) : (
                <>
                  Book a Demo
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </>
              )}
            </button>
            <p className="bd-hint">Only your work email is required. You can leave without picking a time.</p>
          </div>
        </form>
      </div>
    </DemoChrome>
  );
}
